import { Link } from 'react-router-dom';
import SeoHead from '../components/SeoHead';
import GalleryLightbox from '../components/GalleryLightbox';
import { COMPANY } from '../data/seedData';
import { imageUrl } from '../lib/images';

export default function ReparationPage() {
  return (
    <>
      <SeoHead
        title="Udkørende reparation - RB Glasfiber A/S"
        description="Hos RB Glasfiber udføres professionelt reparationsarbejde i glasfiber. Vi rykker ud til reparation af siloer, tanke, både og procesanlæg i hele landet."
        canonical="https://www.rb-glasfiber.dk/rb-glasfiber/reparationer"
        ogImage="https://assets.auxodev.dk/assets/9692fb57-52ae-41f1-9d47-5179b8f3a973/rep-faerch-plast-scaled-e1581422895121-cropresize-412x619.webp"
      />

      {/* Hero Header */}
      <section className="relative pt-32 pb-16 lg:pt-40 lg:pb-24 bg-slate-950 text-white overflow-hidden border-b border-slate-800">
        <div className="absolute inset-0 z-0">
          <img
            src={imageUrl('https://assets.auxodev.dk/assets/9692fb57-52ae-41f1-9d47-5179b8f3a973/rep-faerch-plast-scaled-e1581422895121-cropresize-412x619.webp', 'width=1600,quality=80,format=auto')}
            alt="Udkørende glasfiber reparation"
            className="w-full h-full object-cover opacity-25"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/80 to-slate-950/60" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded bg-[#1e9457]/20 border border-[#1e9457]/40 text-[#22c55e] text-xs font-bold uppercase tracking-widest mb-4">
              <i className="fa-solid fa-wrench"></i>
              <span>Mobil industriservice</span>
            </div>
            <h1 className="text-3xl sm:text-5xl font-extrabold uppercase tracking-tight text-white mb-6">
              Udkørende glasfiber reparation
            </h1>
            <p className="text-base sm:text-lg text-slate-300 leading-relaxed max-w-2xl">
              Hos RB Glasfiber udføres også forskellige former for reparationsarbejde. Vi reparerer bl.a. både, siloer og tanke, men vi kan løse stort set alle former for reparationsarbejde i glasfiber.
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-4">
              <a
                href={`tel:${COMPANY.phoneRaw}`}
                className="inline-flex items-center gap-2.5 px-6 py-3.5 rounded bg-[#1e9457] hover:bg-[#15703f] text-white font-bold uppercase tracking-wider text-sm shadow transition-all whitespace-nowrap"
              >
                <i className="fa-solid fa-phone"></i>
                <span className="whitespace-nowrap">Ring akut: {COMPANY.phone}</span>
              </a>
              <Link
                to="/kontakt"
                className="inline-flex items-center gap-2 px-6 py-3.5 rounded bg-slate-800 hover:bg-slate-700 text-white font-semibold text-sm border border-slate-700 transition-all"
              >
                <span>Send reparationsforespørgsel</span>
                <i className="fa-solid fa-arrow-right text-xs"></i>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Commercial Content */}
      <section className="py-16 bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7 space-y-6">
              <div className="inline-flex items-center gap-2 text-xs font-bold text-[#1e9457] uppercase tracking-wider">
                <span className="w-2 h-2 rounded-full bg-[#1e9457]"></span>
                Erfaring, fleksibilitet og præcision
              </div>
              <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 uppercase">
                Reparationsarbejde i glasfiber
              </h2>
              <div className="prose text-slate-700 text-base leading-relaxed space-y-4">
                <p>
                  Hos RB Glasfiber udføres også forskellige former for reparationsarbejde. Vi reparerer bl.a. både, siloer og tanke, men vi kan løse stort set alle former for reparationsarbejde i glasfiber.
                </p>
                <p>
                  Uanset, hvor stor eller kompleks reparationsopgaven er, er du altid velkommen til at kontakte os for at høre nærmere. Vi har erfaringen og det rette udstyr til at udføre reparationer på stedet – så unødig nedetid og transport undgås.
                </p>
              </div>

              {/* Scope of repairs */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
                <div className="p-4 rounded-lg bg-slate-50 border border-slate-200">
                  <div className="font-bold text-slate-900 mb-1 flex items-center gap-2">
                    <i className="fa-solid fa-warehouse text-[#1e9457]"></i>
                    Industrielle tanke & siloer
                  </div>
                  <p className="text-xs text-slate-600">
                    Udbedring af revner, tæthedsskader, laminering af nye bundkegler og forstærkning i korrosive miljøer.
                  </p>
                </div>

                <div className="p-4 rounded-lg bg-slate-50 border border-slate-200">
                  <div className="font-bold text-slate-900 mb-1 flex items-center gap-2">
                    <i className="fa-solid fa-ship text-[#1e9457]"></i>
                    Både & marineemner
                  </div>
                  <p className="text-xs text-slate-600">
                    Strukturel genopbygning, gel-coat finish og udbedring af osmose- eller støbeskader.
                  </p>
                </div>

                <div className="p-4 rounded-lg bg-slate-50 border border-slate-200">
                  <div className="font-bold text-slate-900 mb-1 flex items-center gap-2">
                    <i className="fa-solid fa-water-ladder text-[#1e9457]"></i>
                    Bassiner & rensningsanlæg
                  </div>
                  <p className="text-xs text-slate-600">
                    Tætning af havebassiner, svømmebade og kemikalieresistente proceskar med vinylester.
                  </p>
                </div>

                <div className="p-4 rounded-lg bg-slate-50 border border-slate-200">
                  <div className="font-bold text-slate-900 mb-1 flex items-center gap-2">
                    <i className="fa-solid fa-car text-[#1e9457]"></i>
                    Special- & veteranbiler
                  </div>
                  <p className="text-xs text-slate-600">
                    Specialiseret reparation af glasfiberkarosserier, som fx dokumenteret på Renault Alpine 310.
                  </p>
                </div>
              </div>
            </div>

            <div className="lg:col-span-5 space-y-4">
              <div className="rounded-xl overflow-hidden border border-slate-200 shadow-md aspect-[4/3] bg-slate-900">
                <img
                  src={imageUrl('https://assets.auxodev.dk/assets/9692fb57-52ae-41f1-9d47-5179b8f3a973/rep-faerch-plast-scaled-e1581422895121-cropresize-412x619.webp', 'width=700,quality=85,format=auto')}
                  alt="Reparation hos Færch Plast"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-4 bg-slate-100 rounded-lg text-xs text-slate-600 border border-slate-200">
                <span className="font-bold text-slate-900">Dokumenteret reference:</span> Udkørende montage og reparation af ny bundkegle hos Færch Plast.
              </div>
            </div>
          </div>

          {/* Quick Call Out */}
          <div className="p-8 rounded-xl bg-slate-900 text-white flex flex-col md:flex-row items-center justify-between gap-6 shadow-xl">
            <div>
              <h3 className="text-xl sm:text-2xl font-bold uppercase tracking-tight text-white">
                Brug for hurtig reparation eller rådgivning?
              </h3>
              <p className="text-slate-300 text-sm mt-1 max-w-xl">
                Ring direkte til Torben Nielsen på telefonen – så vurderer vi opgaven med det samme.
              </p>
            </div>
            <a
              href={`tel:${COMPANY.phoneRaw}`}
              className="px-6 py-3.5 rounded bg-[#1e9457] hover:bg-[#15703f] text-white font-bold uppercase tracking-wider text-sm transition-all whitespace-nowrap shadow flex items-center gap-2 flex-shrink-0"
            >
              <i className="fa-solid fa-phone"></i>
              <span className="whitespace-nowrap">{COMPANY.phone}</span>
            </a>
          </div>

          {/* Lightbox Gallery */}
          <div className="pt-8 border-t border-slate-200">
            <GalleryLightbox
              gallerySlug="reparation"
              title="Fotodokumentation af udførte reparationer"
              description="Her ses eksempler på reparation hos Færch Plast, reparation af havebassin, renovering for Holstebro Kommune og glasfiberarbejde på Renault Alpine 310."
              columns={3}
            />
          </div>
        </div>
      </section>
    </>
  );
}
